/* eslint-disable react/prop-types */
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import RedoIcon from "@mui/icons-material/Redo";
import TitleIcon from "@mui/icons-material/Title";
import UndoIcon from "@mui/icons-material/Undo";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Box,
  Divider,
  FormHelperText,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

const RECIPIENT_FIELDS = [
  { label: "Όνομα παραλήπτη", token: "[RecipientName]" },
  { label: "Email παραλήπτη", token: "[EmailTo]" },
];

const toolbarButtonSx = (active = false) => ({
  width: 32,
  height: 32,
  borderRadius: 1,
  border: "1px solid",
  borderColor: active ? "primary.main" : "divider",
  bgcolor: active ? "action.selected" : "transparent",
});

const ToolButton = ({ title, active, disabled, onClick, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        color={active ? "primary" : "default"}
        disabled={disabled}
        onClick={onClick}
        sx={toolbarButtonSx(active)}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const MailBodyEditor = ({
  value,
  onChange,
  onBlur,
  disabled,
  error,
  helperText,
}) => {
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  const [recipientFieldAnchor, setRecipientFieldAnchor] = useState(null);

  useEffect(() => {
    onChangeRef.current = onChange;
    onBlurRef.current = onBlur;
  }, [onBlur, onChange]);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Underline,
      Link.configure({
        autolink: true,
        defaultProtocol: "https",
        openOnClick: false,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: value || "",
    editable: !disabled,
    onBlur: () => {
      onBlurRef.current?.();
    },
    onUpdate: ({ editor: nextEditor }) => {
      onChangeRef.current(nextEditor.isEmpty ? "" : nextEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;

    editor.setEditable(!disabled);
  }, [disabled, editor]);

  useEffect(() => {
    if (!editor) return;

    const nextValue = value || "";
    if (editor.getHTML() !== nextValue) {
      editor.commands.setContent(nextValue, { emitUpdate: false });
    }
  }, [editor, value]);

  const runCommand = (command) => {
    if (!editor || disabled) return;
    command();
  };

  const setLink = () => {
    if (!editor || disabled) return;

    const previousUrl = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Διεύθυνση συνδέσμου", previousUrl);

    if (url === null) return;

    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url.trim() })
      .run();
  };

  const toolbarDisabled = disabled || !editor;
  const recipientFieldMenuOpen = Boolean(recipientFieldAnchor);

  const insertRecipientField = (token) => {
    if (!editor || disabled) return;

    editor.chain().focus().insertContent(token).run();
    setRecipientFieldAnchor(null);
  };

  return (
    <Box>
      <Box
        sx={{
          border: "1px solid",
          borderColor: error ? "error.main" : "divider",
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: disabled ? "action.disabledBackground" : "background.paper",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          sx={{
            px: 1,
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <ToolButton
            title="Αναίρεση"
            disabled={toolbarDisabled}
            onClick={() => runCommand(() => editor.chain().focus().undo().run())}
          >
            <UndoIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Επανάληψη"
            disabled={toolbarDisabled}
            onClick={() => runCommand(() => editor.chain().focus().redo().run())}
          >
            <RedoIcon fontSize="small" />
          </ToolButton>

          <Divider flexItem orientation="vertical" />

          <ToolButton
            title="Έντονα"
            active={editor?.isActive("bold")}
            disabled={toolbarDisabled}
            onClick={() => runCommand(() => editor.chain().focus().toggleBold().run())}
          >
            <FormatBoldIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Πλάγια"
            active={editor?.isActive("italic")}
            disabled={toolbarDisabled}
            onClick={() => runCommand(() => editor.chain().focus().toggleItalic().run())}
          >
            <FormatItalicIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Υπογράμμιση"
            active={editor?.isActive("underline")}
            disabled={toolbarDisabled}
            onClick={() => runCommand(() => editor.chain().focus().toggleUnderline().run())}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Διακριτή διαγραφή"
            active={editor?.isActive("strike")}
            disabled={toolbarDisabled}
            onClick={() => runCommand(() => editor.chain().focus().toggleStrike().run())}
          >
            <FormatStrikethroughIcon fontSize="small" />
          </ToolButton>

          <Divider flexItem orientation="vertical" />

          <ToolButton
            title="Επικεφαλίδα"
            active={editor?.isActive("heading", { level: 2 })}
            disabled={toolbarDisabled}
            onClick={() =>
              runCommand(() => editor.chain().focus().toggleHeading({ level: 2 }).run())
            }
          >
            <TitleIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Λίστα"
            active={editor?.isActive("bulletList")}
            disabled={toolbarDisabled}
            onClick={() => runCommand(() => editor.chain().focus().toggleBulletList().run())}
          >
            <FormatListBulletedIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Αριθμημένη λίστα"
            active={editor?.isActive("orderedList")}
            disabled={toolbarDisabled}
            onClick={() => runCommand(() => editor.chain().focus().toggleOrderedList().run())}
          >
            <FormatListNumberedIcon fontSize="small" />
          </ToolButton>

          <Divider flexItem orientation="vertical" />


          <Menu
            anchorEl={recipientFieldAnchor}
            open={recipientFieldMenuOpen}
            onClose={() => setRecipientFieldAnchor(null)}
          >
            {RECIPIENT_FIELDS.map((field) => (
              <MenuItem
                key={field.token}
                onClick={() => insertRecipientField(field.token)}
              >
                {field.label}
              </MenuItem>
            ))}
          </Menu>

          <Divider flexItem orientation="vertical" />

          <ToolButton
            title="Σύνδεσμος"
            active={editor?.isActive("link")}
            disabled={toolbarDisabled}
            onClick={setLink}
          >
            <LinkIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Αφαίρεση συνδέσμου"
            disabled={toolbarDisabled || !editor?.isActive("link")}
            onClick={() =>
              runCommand(() => editor.chain().focus().extendMarkRange("link").unsetLink().run())
            }
          >
            <LinkOffIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Καθαρισμός μορφοποίησης"
            disabled={toolbarDisabled}
            onClick={() =>
              runCommand(() => editor.chain().focus().unsetAllMarks().clearNodes().run())
            }
          >
            <FormatClearIcon fontSize="small" />
          </ToolButton>
          <div style={{ flex: 1 }} />
          <ToolButton
            title="Πεδία παραλήπτη"
            active={recipientFieldMenuOpen}
            disabled={toolbarDisabled}
            onClick={(event) => setRecipientFieldAnchor(event.currentTarget)}
          >
            <PersonAddAltIcon fontSize="small" />
          </ToolButton>
        </Stack>

        <Box
          sx={{
            "& .mail-body-editor__content": {
              minHeight: 430,
            },
            "& .ProseMirror": {
              minHeight: 430,
              px: 2,
              py: 1.5,
              outline: "none",
              fontFamily: "inherit",
              fontSize: 14,
              lineHeight: 1.65,
              "& p": {
                m: 0,
              },
              "& h2, & h3": {
                mt: 1.5,
                mb: 1,
                lineHeight: 1.25,
              },
              "& ul": {
                my: 0,
                pl: 3,
                listStylePosition: "outside",
                listStyleType: "disc",
              },
              "& ol": {
                my: 0,
                pl: 3,
                listStylePosition: "outside",
                listStyleType: "decimal",
              },
              "& li": {
                display: "list-item",
                pl: 0.5,
              },
              "& ul > li": {
                listStyleType: "disc",
              },
              "& ol > li": {
                listStyleType: "decimal",
              },
              "& li p": {
                m: 0,
              },
              "& a": {
                color: "primary.main",
                textDecoration: "underline",
              },
            },
          }}
        >
          <EditorContent className="mail-body-editor__content" editor={editor} />
        </Box>
      </Box>

      {helperText && (
        <FormHelperText error={error} sx={{ mx: 1.5 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default MailBodyEditor;
